import React, { Fragment } from "react";
import T from "prop-types";
import styled from "styled-components/macro";
import TextInput from "commons/components/TextInput";
import Icon from "commons/components/Icon";
import Flexbox from "commons/components/Flexbox";
import AddButton from "commons/components/AddButton";
import Checkbox from "commons/components/Checkbox";
import { useTranslation } from "react-i18next";
import Typography from "commons/components/Typography";
import Popover from "commons/components/Popover";
import TagCreatePopoverContent from "./TagCreatePopoverContent";
import Divider from "commons/components/Divider";
import EmptyState from "commons/components/EmptyState";
import noTagsImage from "commons/images/emptyState/tags.svg";
import { getTagGroupColor } from "commons/util/helpers";
import noSearchResultsImage from "commons/images/emptyState/searchResults.svg";

const Wrap = styled.div`
  position: relative;
  height: 480px;
  width: 376px;
`;

const Box = styled.div`
  position: relative;
  width: 100%;
  height: calc(100% - 45px);
  overflow: auto;
  background-color: var(--neutral-200);
  border-radius: var(--border-radius-1) var(--border-radius-1) 0 0;
`;

const SearchWrap = styled.div`
  position: sticky;
  top: 0px;
  border-bottom: 1px solid var(--neutral-180);
  background-color: var(--neutral-200);
  padding: 8px;
`;

const Options = styled.div`
  overflow: auto;
  padding-bottom: 40px;
`;

const Items = styled.div`
  padding: 8px;
`;

const Category = styled(Flexbox)`
  padding: 16px;
  border-radius: var(--border-radius-0);
  cursor: pointer;

  &:hover {
    background-color: var(--neutral-190);
  }
`;

const Item = styled(Flexbox)`
  padding: 8px;
  border-radius: var(--border-radius-1);
  cursor: pointer;

  &:hover {
    background-color: var(--neutral-190);
  }
`;

const AddButtonStyled = styled(AddButton)`
  margin-top: 8px;
`;

const CheckboxStyled = styled(Checkbox)`
  align-items: center;

  .check {
    margin-top: 0;
  }
`;

const Count = styled.span`
  color: var(--neutral-120);
`;

const BottomButtons = styled(Flexbox)`
  background-color: var(--neutral-190);
  border-radius: var(--border-radius-0) var(--border-radius-0) var(--border-radius-1) var(--border-radius-1);
  border-top: 1px solid var(--neutral-180);
`;

const BottomButton = styled(Flexbox)`
  padding: 12px 16px;
  font-size: 15px;
  cursor: pointer;
  color: var(--neutral-140);
  border-radius: var(--border-radius-0);

  &:hover {
    background-color: var(--neutral-180);
    color: var(--neutral-120);
  }
`;

function TagInputPopoverContent({
  className,
  searchPhrase,
  setSearchPhrase,
  filterOptions,
  handleCategoryClick,
  handleCheckboxClick,
  addTag,
  clearFilter,
  isLoading,
  tagGroups,
}) {
  const { t } = useTranslation();

  return (
    <Wrap>
      <Box className={className}>
        <SearchWrap>
          <TextInput
            placeholder={t("Tags.SearchTags")}
            value={searchPhrase}
            onChange={setSearchPhrase}
            leftIcon="search"
            rightIcon={searchPhrase ? "close" : ""}
            onRightIconClick={() => setSearchPhrase("")}
            maxLength={32}
          />
        </SearchWrap>
        {!!filterOptions.length && (
          <Options>
            {filterOptions.map(option => {
              return (
                <Fragment key={option.id}>
                  <Category
                    justifyContent="space-between"
                    onClick={() => handleCategoryClick(option.id)}
                    isExpanded={option.isExpanded}
                  >
                    <Typography variant="body">{option.label}</Typography>
                    <Icon name={option.isExpanded ? "expand_less" : "expand_more"} size={20} />
                  </Category>
                  {option.isExpanded && (
                    <Items>
                      {option.items.map(item => (
                        <Item
                          key={item.id}
                          justifyContent="space-between"
                          onClick={() => handleCheckboxClick(option.id, item.id, item.isChecked)}
                        >
                          <CheckboxStyled label={item.label} isChecked={item.isChecked} />
                          <Count>{item.count}</Count>
                        </Item>
                      ))}
                      <Popover
                        trigger={<AddButtonStyled fullWidth>{t("Tags.AddTag")}</AddButtonStyled>}
                        content={closePopover => (
                          <TagCreatePopoverContent
                            color={getTagGroupColor(tagGroups.find(el => el.id === option.id))}
                            addTag={tagData => addTag(option.id, tagData)}
                            closePopover={closePopover}
                          />
                        )}
                      />
                    </Items>
                  )}
                  <Divider />
                </Fragment>
              );
            })}
          </Options>
        )}
        {!isLoading && !filterOptions.length && !searchPhrase && (
          <EmptyState image={noTagsImage} label={t("Tags.NoTags")} height="360px" />
        )}
        {!isLoading && !filterOptions.length && searchPhrase && (
          <EmptyState image={noSearchResultsImage} label={t("Common.NoSearchResults")} height="360px" />
        )}
      </Box>
      <BottomButtons justifyContent="space-between">
        <BottomButton forwardedAs="button" gap={8} flexBasis="50%" alignItems="center" onClick={clearFilter}>
          <Icon name="playlist_remove" size={20} />
          {t("Tags.ClearSelection")}
        </BottomButton>
        <BottomButton
          forwardedAs="a"
          gap={8}
          flexBasis="50%"
          alignItems="center"
          href="/app/settings/tags"
          target="_blank"
        >
          <Icon name="open_in_new" size={20} />
          {t("Tags.ManageTags")}
        </BottomButton>
      </BottomButtons>
    </Wrap>
  );
}

TagInputPopoverContent.propTypes = {
  className: T.string,
  addTag: T.func,
  searchPhrase: T.string,
  setSearchPhrase: T.func,
  filterOptions: T.array,
  handleCategoryClick: T.func,
  handleCheckboxClick: T.func,
  clearFilter: T.func,
  isLoading: T.bool,
  tagGroups: T.array,
};

export default TagInputPopoverContent;
